import { API_BASE_URL } from '../../../api/config';
import fetchService from '../../../services/fetchService';
import { createFileLogger } from '../../../utils/logger';
import { CredentialEntity, CredentialType, CredentialFields } from '../types/entity';

const BASE = `${API_BASE_URL}/credentials`;
const ENTITIES_BASE = `${API_BASE_URL}/entities`;

const log = createFileLogger('CREDENTIALS_SERVICE');

export interface CreateCredentialPayload {
  name: string;
  credentialType: CredentialType;
  fields: Record<string, string>;
  service?: string;
  url?: string;
  username?: string;
  description?: string;
  tags?: string[];
}

export interface UpdateCredentialPayload {
  name?: string;
  fields?: Record<string, string>;
  service?: string;
  url?: string;
  username?: string;
  description?: string;
  tags?: string[];
  /** Set to entity ID to link, or empty string to unlink */
  linkedEntityId?: string;
}

/**
 * Normalize a service slug the same way the backend extraction does, so
 * credential → Service matching works regardless of casing or formatting.
 */
function normalizeServiceSlug(value: string): string {
  if (!value) return '';
  let s = value.trim().toLowerCase();
  s = s.replace(/^https?:\/\//, '');
  s = s.split('/')[0];
  s = s.replace(/^www\./, '');
  s = s.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return s.slice(0, 64);
}

/**
 * Fire-and-forget: after a credential is created, try to find a matching
 * Service entity and back-link it (credentialStatus='saved' + credentialEntityId).
 *
 * Any failure here is non-fatal — the credential still exists. The Service
 * Audit pipeline will reconcile on its next run.
 */
async function linkCredentialToService(credential: CredentialEntity): Promise<void> {
  const slug = normalizeServiceSlug(credential.skeleton?.service || credential.name || '');
  if (!slug) {
    log.info('[credentialsService] linkCredentialToService: no service slug — skipping');
    return;
  }

  try {
    // Look for a Service whose slug matches this credential's service.
    const params = new URLSearchParams({
      entityType: 'Service',
      'skeleton.slug': slug,
      limit: '5',
    });
    const matches: any = await fetchService(`${ENTITIES_BASE}?${params}`, { method: 'GET' });
    const list: any[] = Array.isArray(matches) ? matches : (matches?.entities ?? []);
    const match = list.find((e) => (e?.skeleton?.slug || '').toLowerCase() === slug);
    if (!match?._id) {
      log.info(`[credentialsService] linkCredentialToService: no Service entity for slug='${slug}'`);
      return;
    }

    await fetchService(`${ENTITIES_BASE}/${match._id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        skeleton: {
          credentialStatus: 'saved',
          credentialEntityId: credential._id,
        },
      }),
    });
    log.info(
      `[credentialsService] linkCredentialToService: linked credential=${credential._id} → service=${match._id} slug=${slug}`,
    );
  } catch (err) {
    log.warn('[credentialsService] linkCredentialToService failed (non-fatal):', err);
  }
}

export const credentialsService = {
  async list(filters?: { credentialType?: CredentialType; service?: string }): Promise<CredentialEntity[]> {
    const params = new URLSearchParams();
    if (filters?.credentialType) params.set('credentialType', filters.credentialType);
    if (filters?.service) params.set('service', filters.service);
    const url = params.toString() ? `${BASE}?${params}` : BASE;
    return fetchService(url, { method: 'GET' });
  },

  async get(id: string): Promise<CredentialEntity & { skeleton: { fields: CredentialFields } }> {
    return fetchService(`${BASE}/${id}`, { method: 'GET' });
  },

  async create(payload: CreateCredentialPayload): Promise<CredentialEntity> {
    const created: CredentialEntity = await fetchService(BASE, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    // Fire-and-forget Service linkage — never blocks the caller.
    linkCredentialToService(created).catch((err) =>
      log.warn('[credentialsService] linkCredentialToService unhandled:', err),
    );
    return created;
  },

  async update(id: string, payload: UpdateCredentialPayload): Promise<CredentialEntity> {
    return fetchService(`${BASE}/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
  },

  async delete(id: string): Promise<void> {
    await fetchService(`${BASE}/${id}`, { method: 'DELETE' });
  },
};
